// oxlint-disable prefer-arrow-callback -- callbacks below must be named functions; logAnalytics reads callback.name
// Utils
import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { isExtensionOnlyStackTrace } from '../../common/monitoring/isExtensionOnlyStackTrace';
import globalLogger, { LoggerComponents } from '../logger';
import { runIfAvailable } from './analyticsInvocationUtils';
import { getNewRelicBrowserAgent } from './newRelicAgentState';
import { setNewRelicUserIdCapacitor } from './newRelicCapacitorUtils';
import { NewRelicCustomAttributes, NewRelicPageActions } from './newRelicConstants';

const logger = globalLogger.createChannel(LoggerComponents.ANALYTICS);

type NewRelicAttributeValue = string | boolean | number | null;
type NewRelicAttributes = Record<string, NewRelicAttributeValue>;

// Narrowed to what this file calls, so a fake agent can be a plain object literal instead of a cast.
export type NewRelicAgentApi = Pick<
    BrowserAgent,
    'setApplicationVersion' | 'setUserId' | 'setCustomAttribute' | 'finished' | 'addPageAction' | 'setErrorHandler'
>;

const isNewRelicAvailable = (): boolean => !!getNewRelicBrowserAgent();

// V8 is the only engine that prefixes every frame with "at "; Firefox/Safari never do, since they have no
// message header line to distinguish frames from. So rather than inspecting line 1's own shape (ambiguous -
// a message can itself contain "@", e.g. mentioning an email address, and get mistaken for an "@"-style frame),
// check whether ANY other line uses V8's "at " syntax: if one does, this is a V8 stack and line 1 is its
// header; if none do, every line - including the first - is a real Firefox/Safari frame.
const V8_FRAME_LINE_PATTERN = /^\s*at\s/i;

/**
 * Pulls the URL out of each `Error.stack` frame line, dropping the leading message line only for a V8
 * stack - Firefox/Safari stacks have no message line and start straight on a frame.
 * An unparseable frame becomes `undefined` rather than being dropped, so it still
 * counts as non-extension - matching Rollbar's adapter.
 */
const getStackFrameUrls = (stack: string | undefined): Array<string | undefined> => {
    if (!stack) return [];

    const lines = stack.split('\n');
    const isV8Stack = lines.slice(1).some((line) => V8_FRAME_LINE_PATTERN.test(line));
    const frameLines = isV8Stack ? lines.slice(1) : lines;

    const frameUrlPattern = /((?:[a-z-]+-extension|https?):\/\/[^\s)]+)/i;

    return frameLines.map((line) => line.match(frameUrlPattern)?.[1]);
};

/** Runs `callback` with the live agent, only if one is available, logging either way. */
const runIfNewRelicIsAvailable = <T extends unknown[]>(callback: (agent: NewRelicAgentApi, ...args: T) => void) => {
    const wrapped = (...args: T): void => {
        const agent = getNewRelicBrowserAgent();
        if (agent) callback(agent, ...args);
    };
    // Copies callback's name onto this otherwise-anonymous wrapper, since logAnalytics reads it.
    Object.defineProperty(wrapped, 'name', { value: callback.name });
    return runIfAvailable('New Relic', isNewRelicAvailable)(wrapped);
};

/**
 * Set the application version (`application.version`) for page actions and browser interactions in New Relic.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setApplicationVersion/
 */
export const setNewRelicApplicationVersion = runIfNewRelicIsAvailable(function setNewRelicApplicationVersion(
    agent: NewRelicAgentApi,
    applicationVersion: string | null,
): void {
    agent.setApplicationVersion(applicationVersion);
});

/**
 * Set the User ID (`enduser.id`) for page actions and browser interactions in New Relic.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setuserid/
 */
export const setNewRelicUserId = (userId: string | null) => {
    // JS library
    runIfNewRelicIsAvailable(function setNewRelicUserId(agent: NewRelicAgentApi, userId: string | null): void {
        agent.setUserId(userId || 'guest');
    })(userId);

    // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
    void setNewRelicUserIdCapacitor(userId).catch((error: unknown) => {
        logger.warn('Failed to set New Relic user ID on the Capacitor plugin', error);
    });
};

/**
 * Submits a custom attribute to New Relic.
 * All future browser interaction events or page actions will include this attribute.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setcustomattribute/
 */
export const setNewRelicCustomAttribute = runIfNewRelicIsAvailable(function setNewRelicCustomAttribute(
    agent: NewRelicAgentApi,
    key: NewRelicCustomAttributes,
    value: NewRelicAttributeValue,
): void {
    agent.setCustomAttribute(key, value);
});

/**
 * Submits a custom attribute to New Relic.
 * All future browser interaction events or page actions will include this attribute.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setcustomattribute/
 */
export const setNewRelicMultipleCustomAttributes = runIfNewRelicIsAvailable(function setNewRelicCustomAttribute(
    agent: NewRelicAgentApi,
    attributesObject: NewRelicAttributes,
): void {
    for (const key in attributesObject) {
        agent.setCustomAttribute(key, attributesObject[key]);
    }
});

/**
 * Submits a PageAction event with the name "finished" to New Relic.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/finished/
 */
export const sendNewRelicPageLoadFinishedAction = runIfNewRelicIsAvailable(function sendNewRelicPageLoadFinishedAction(
    agent: NewRelicAgentApi,
    initialisationTime: number,
): void {
    agent.finished(initialisationTime);
});

/**
 * Submits a PageAction event to New Relic.
 * This should be used for custom events that are not covered by the standard New Relic events.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/addpageaction/
 */
export const sendNewRelicPageAction = runIfNewRelicIsAvailable(function sendNewRelicPageAction(
    agent: NewRelicAgentApi,
    actionName: NewRelicPageActions,
    attributes: NewRelicAttributes = {},
): void {
    agent.addPageAction(actionName, attributes);
});

/**
 * Registers a New Relic error handler that drops errors from other browser extensions.
 * Shares its matching logic with Rollbar's `checkIgnore` - see isExtensionOnlyStackTrace.ts.
 *
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/seterrorhandler/
 */
export const setNewRelicErrorHandler = runIfNewRelicIsAvailable(function setNewRelicErrorHandler(
    agent: NewRelicAgentApi,
): void {
    // New Relic can hand this a bare string instead of an Error - those never have a stack, so they're kept.
    agent.setErrorHandler((error: string | Error): boolean => {
        const stack = typeof error === 'string' ? undefined : error?.stack;
        const frameUrls = getStackFrameUrls(stack);
        return isExtensionOnlyStackTrace(frameUrls);
    });
});
